import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Language = 'en' | 'es';
type TranslationParams = Record<string, string | number | undefined | null>;

const dictionaries: Record<Language, Record<string, string>> = {
  en: {
    'common.loading': 'Loading...',
    'common.saving': 'Saving...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.remove': 'Remove',
    'common.current': 'Current',
    'common.unknown': 'Unknown',
    'common.never': 'Never',
    'common.currentSession': 'Current session',
    'common.signOut': 'Sign Out',
    'common.lightMode': 'Light Mode',
    'common.darkMode': 'Dark Mode',
    'common.accountSettings': 'Account Settings',
    'common.change': 'Change',
    'common.update': 'Update',
    'common.dismiss': 'Dismiss',
    'common.yes': 'Yes',
    'common.no': 'No',

    'nav.workspace': 'Workspace',
    'nav.timekeeping': 'Timekeeping',
    'nav.projects': 'Projects',
    'nav.production': 'Production',
    'nav.team': 'Team',
    'nav.admin': 'Admin',
    'nav.guided': 'My day',
    'nav.general': 'General',
    'nav.operations': 'Operations',
    'nav.resources': 'Resources',
    'nav.analysis': 'Analysis',
    'nav.administration': 'Administration',
    'nav.inProgressGroup': 'In Progress',
    'nav.dashboard': 'Dashboard',
    'nav.timesheets': 'Timesheets',
    'nav.schedule': 'Schedule',
    'nav.reports': 'Reports',
    'nav.employeeHours': 'Emp. Hours',
    'nav.field_logs': 'Field Reports',
    'nav.control': 'Control',
    'nav.people': 'People',
    'nav.exports': 'Exports',
    'nav.security': 'Security',
    'nav.activityLog': 'Activity Log',
    'nav.settings': 'Settings',
    'nav.in_progress': 'In Progress',
    'nav.warehouse': 'Warehouse',
    'nav.requests': 'Requests',
    'nav.account': 'Account',

    'layout.builtForField': 'Built for the field.',
    'layout.poweredByForxa': 'Powered by Forxa',
    'layout.expandSidebar': 'Expand sidebar',
    'layout.collapseSidebar': 'Collapse sidebar',
    'layout.notifications': 'Notifications',
    'layout.unreadCount': '{{count}} unread',
    'layout.markAllRead': 'Mark all read',
    'layout.noNotifications': 'No notifications yet',
    'layout.justNow': 'Just now',
    'layout.minutesAgo': '{{count}}m ago',
    'layout.hoursAgo': '{{count}}h ago',
    'layout.daysAgo': '{{count}}d ago',
    'layout.signOut': 'Sign out',
    'layout.switchToLight': 'Switch to light mode',
    'layout.switchToDark': 'Switch to dark mode',

    'account.role.developer': 'Developer',
    'account.role.operator': 'Operator',
    'account.role.admin': 'Admin',
    'account.role.supervisor': 'Supervisor',
    'account.companyManaged': 'Company branding and company-wide settings are managed separately from your personal account.',
    'account.profilePreferences': 'Profile & Preferences',
    'account.profilePreferencesHint': 'Personal details and how the app should feel when you use it.',
    'account.displayName': 'Display name',
    'account.displayNameHint': 'Shown in the account menu and across the app.',
    'account.displayNamePlaceholder': 'How you want your name to appear',
    'account.language': 'Language',
    'account.language.english': 'English',
    'account.language.spanish': 'Spanish',
    'account.timezone': 'Timezone',
    'account.dateFormat': 'Date format',
    'account.theme': 'Theme',
    'account.theme.light': 'Light',
    'account.theme.dark': 'Dark',
    'account.theme.system': 'System',
    'account.startPage': 'Start page',
    'account.startPageHint': 'Where you want to land after sign-in.',
    'account.savePreferences': 'Save Preferences',
    'account.preferencesUpdated': 'Account preferences updated',
    'account.preferencesUpdateFailed': 'Failed to update account preferences',
    'account.photo': 'Profile Photo',
    'account.photoHint': 'Shown in the top-right avatar and account panel. We auto-crop it to a centered avatar before saving.',
    'account.photoUpload': 'Upload photo',
    'account.photoChange': 'Change photo',
    'account.photoSave': 'Save Photo',
    'account.photoUpdated': 'Profile photo updated',
    'account.photoRemoved': 'Profile photo removed',
    'account.photoUpdateFailed': 'Failed to update photo',
    'account.photoTooLarge': 'Photo must be under 5 MB',
    'account.photoProcessed': 'Photo cropped and optimized',
    'account.photoProcessFailed': 'Failed to process photo',
    'account.notifications': 'Notification Preferences',
    'account.notificationsHint': 'Control which events trigger notifications and how you receive them.',
    'account.inappSection': 'In-App Notifications',
    'account.inappSectionHint': 'Choose which alerts appear in the notification bell.',
    'account.emailSection': 'Email Notifications',
    'account.emailSectionHint': 'Choose which alerts are also sent to your email address.',
    'account.notifyMissingTimesheets': 'Missing timesheet reminders',
    'account.notifyMissingTimesheetsHint': 'Alerts when your team still has missing timesheets.',
    'account.notifyStatusChanges': 'Timesheet approved / rejected',
    'account.notifyStatusChangesHint': 'Status-change alerts for timesheets that concern you.',
    'account.notifyProjectAssignments': 'Project assignment changes',
    'account.notifyProjectAssignmentsHint': 'Get notified when you are assigned to or removed from a project.',
    'account.notifyProjectDelays': 'Project delay / risk alerts',
    'account.notifyProjectDelaysHint': 'Heads-up when a project falls behind or gets flagged as risky.',
    'account.notifyMaintenance': 'Maintenance alerts',
    'account.notifyMaintenanceHint': 'See advance warnings before scheduled downtime starts.',
    'account.notifySecurity': 'Security activity alerts',
    'account.notifySecurityHint': 'Suspicious sign-ins, revoked sessions, and other account security events.',
    'account.pendingApprovalDays': 'Pending approval alert (days)',
    'account.pendingApprovalDaysHint': 'How long something can sit before you want a reminder.',
    'account.saveNotifications': 'Save Notification Preferences',
    'account.notificationsUpdated': 'Notification preferences updated',
    'account.notificationsUpdateFailed': 'Failed to update notifications',
    'account.security': 'Account Security',
    'account.securityHint': 'Current session, recognized devices, and recent sign-in activity.',
    'account.signOutAllDevices': 'Sign Out All Devices',
    'account.signingOut': 'Signing out...',
    'account.lastSuccessfulLogin': 'Last successful login',
    'account.activeSessions': 'Active sessions',
    'account.currentDeviceIdentified': 'Current device identified',
    'account.sessionTrackingActive': 'Session tracking active',
    'account.recognizedDevices': 'Recognized devices',
    'account.noDevices': 'No devices recorded yet.',
    'account.unnamedDevice': 'Unnamed device',
    'account.lastSeen': 'Last seen {{value}}',
    'account.ipLabel': 'IP {{value}}',
    'account.sessionCount': '{{count}} session',
    'account.sessionCountPlural': '{{count}} sessions',
    'account.loadingSecurity': 'Loading security overview...',
    'account.loadingSessions': 'Loading sessions...',
    'account.loadingActivity': 'Loading activity...',
    'account.changePassword': 'Change Password',
    'account.currentPassword': 'Current password',
    'account.currentPasswordPlaceholder': 'Your current password',
    'account.newPassword': 'New password',
    'account.newPasswordPlaceholder': 'Min. 8 characters',
    'account.confirmNewPassword': 'Confirm new password',
    'account.confirmNewPasswordPlaceholder': 'Repeat new password',
    'account.updatePassword': 'Update Password',
    'account.passwordUpdated': 'Password updated',
    'account.passwordUpdateFailed': 'Failed to update password',
    'account.currentPasswordRequired': 'Enter your current password',
    'account.passwordMinLength': 'New password must be at least 8 characters',
    'account.passwordMismatch': 'Passwords do not match',
    'account.activeSessionsTitle': 'Active Sessions',
    'account.activeSessionsHint': 'Review where your account is currently signed in and revoke anything you do not recognize.',
    'account.noSessions': 'No active sessions found.',
    'account.sessionLabel': 'Session {{id}}',
    'account.lastUsed': 'Last used {{value}}',
    'account.expires': 'Expires {{value}}',
    'account.revoke': 'Revoke',
    'account.revoking': 'Revoking...',
    'account.sessionRevoked': 'Session revoked',
    'account.sessionRevokeFailed': 'Failed to revoke session',
    'account.signOutAllFailed': 'Failed to sign out all devices',
    'account.recentSecurityActivity': 'Recent Security Activity',
    'account.recentSecurityActivityHint': 'Recent sign-ins, sign-outs, refreshes, and password changes for this account.',
    'account.noSecurityActivity': 'No recent security activity found.',
    'account.securityAction.login': 'Sign-in',
    'account.securityAction.logout': 'Sign-out',
    'account.securityAction.refresh': 'Session refresh',
    'account.securityAction.password_change': 'Password change',
    'account.securityAction.revoke_session': 'Session revoked',
    'account.failedToLoadSecurity': 'Failed to load account security',

    'startPage.default': 'Default landing',
    'startPage.dashboard': 'Dashboard',
    'startPage.timesheets': 'Timesheets',
    'startPage.projects': 'Projects',
    'startPage.schedule': 'Schedule',
    'startPage.people': 'People',
    'startPage.reports': 'Reports',
    'startPage.fieldLogs': 'Field Reports',
    'startPage.control': 'Control',
    'startPage.employeeHours': 'Employee Hours',
    'startPage.exports': 'Exports',
    'startPage.settings': 'Settings',
    'startPage.security': 'Security Center',

    'login.welcomeBack': 'Welcome back',
    'login.titlePrefix': 'Sign in to',
    'login.titleAccent': 'your jobsite.',
    'login.subtitle': 'Crews, budgets, schedules and field reports - all wired together. One login.',
    'login.tagline': 'Forge what you build.',
    'login.step': 'Step',
    'login.emailStepTitle': 'Email address',
    'login.passwordStepTitle': 'Enter password',
    'login.sessionExpired': 'Your session expired. Please sign in again.',
    'login.tryAgainIn': 'Try again in {{value}}.',
    'login.slowNotice': 'Signing you in. This is taking longer than usual.',
    'login.emailPlaceholder': 'you@company.com',
    'login.passwordPlaceholder': '********',
    'login.changeEmail': 'Change email',
    'login.signingIn': 'Signing in...',
    'login.continue': 'Continue',
    'login.locked': 'Locked',
    'login.connecting': 'Connecting',
    'login.session': 'Session',
    'login.waitAndRetry': 'If the page does not continue, wait a moment and try again. Your session expires after inactivity and will ask you to sign in again.',
    'login.waitAndRetryShort': 'If the page does not continue, wait a moment and try again. Your session may ask you to sign in again after inactivity.',
    'login.status.rateLimited': 'Too many attempts for this email. Try again in {{value}}.',
    'login.status.slow': 'Signing you in. The server is responding more slowly than usual.',
    'login.status.timeout': 'The request timed out before the server answered.',
    'login.status.network': 'We could not reach the server. Check the connection and try again.',

    'error.forbidden.label': 'ACCESS DENIED',
    'error.forbidden.headline': "You're not cleared for this area.",
    'error.forbidden.message': "Your account doesn't have permission to view this page. Contact your administrator if you think this is a mistake.",
    'error.server.label': 'SERVICE UNAVAILABLE',
    'error.server.headline': "We're under the helmet for a minute.",
    'error.server.message': "Forxa can't reach the backend right now. Make sure the server is running and try again in a moment.",
    'error.data.label': 'DATA ERROR',
    'error.data.headline': 'Something came back wrong.',
    'error.data.message': "The request completed but the response wasn't valid. Refresh the page or try again - if it keeps happening, check the backend.",
    'error.default.label': 'NOT FOUND',
    'error.default.headline': 'Nothing here but dirt and gravel.',
    'error.default.message': "The page you're looking for doesn't exist or was moved. Head back to the dashboard and try again.",
    'error.dashboard': 'Dashboard',
    'error.goBack': 'Go Back',
    'error.weWillBeBack': "We'll be back at {{value}}",
    'error.footer': 'Error - {{code}} - Forxa',

    'maintenance.inProgress': 'Maintenance in progress',
    'maintenance.unavailable': 'The system is temporarily unavailable.',
    'maintenance.backAt': 'Back at {{value}}.',
    'maintenance.scheduled': 'Scheduled maintenance {{value}}',
    'maintenance.shortly': 'shortly',
    'maintenance.inHoursMinutes': 'in {{hours}}h {{minutes}}m',
    'maintenance.inMinutes': 'in {{minutes}}m',
  },
  es: {
    'common.loading': 'Cargando...',
    'common.saving': 'Guardando...',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.remove': 'Eliminar',
    'common.current': 'Actual',
    'common.unknown': 'Desconocido',
    'common.never': 'Nunca',
    'common.currentSession': 'Sesion actual',
    'common.signOut': 'Cerrar sesion',
    'common.lightMode': 'Modo claro',
    'common.darkMode': 'Modo oscuro',
    'common.accountSettings': 'Ajustes de la cuenta',
    'common.change': 'Cambiar',
    'common.update': 'Actualizar',
    'common.dismiss': 'Cerrar',
    'common.yes': 'Si',
    'common.no': 'No',

    'nav.workspace': 'Espacio de trabajo',
    'nav.timekeeping': 'Control de tiempo',
    'nav.projects': 'Proyectos',
    'nav.production': 'Produccion',
    'nav.team': 'Equipo',
    'nav.admin': 'Administracion',
    'nav.guided': 'Mi día',
    'nav.general': 'General',
    'nav.operations': 'Operaciones',
    'nav.resources': 'Recursos',
    'nav.analysis': 'Análisis',
    'nav.administration': 'Administración',
    'nav.inProgressGroup': 'En progreso',
    'nav.dashboard': 'Panel',
    'nav.timesheets': 'Timesheets',
    'nav.schedule': 'Cronograma',
    'nav.reports': 'Reportes',
    'nav.employeeHours': 'Horas',
    'nav.field_logs': 'Reportes de Campo',
    'nav.control': 'Control',
    'nav.people': 'Personal',
    'nav.exports': 'Exportaciones',
    'nav.security': 'Seguridad',
    'nav.activityLog': 'Registro de actividad',
    'nav.settings': 'Ajustes',
    'nav.in_progress': 'En progreso',
    'nav.warehouse': 'Almacén',
    'nav.requests': 'Solicitudes',
    'nav.account': 'Cuenta',

    'layout.builtForField': 'Hecho para el campo.',
    'layout.poweredByForxa': 'Impulsado por Forxa',
    'layout.expandSidebar': 'Expandir barra lateral',
    'layout.collapseSidebar': 'Colapsar barra lateral',
    'layout.notifications': 'Notificaciones',
    'layout.unreadCount': '{{count}} sin leer',
    'layout.markAllRead': 'Marcar todo como leido',
    'layout.noNotifications': 'Aun no hay notificaciones',
    'layout.justNow': 'Justo ahora',
    'layout.minutesAgo': 'hace {{count}} min',
    'layout.hoursAgo': 'hace {{count}} h',
    'layout.daysAgo': 'hace {{count}} d',
    'layout.signOut': 'Cerrar sesion',
    'layout.switchToLight': 'Cambiar a modo claro',
    'layout.switchToDark': 'Cambiar a modo oscuro',

    'account.role.developer': 'Developer',
    'account.role.operator': 'Operador',
    'account.role.admin': 'Admin',
    'account.role.supervisor': 'Supervisor',
    'account.companyManaged': 'La marca de la empresa y los ajustes globales se administran por separado de tu cuenta personal.',
    'account.profilePreferences': 'Perfil y preferencias',
    'account.profilePreferencesHint': 'Tus datos personales y como debe sentirse la app cuando la usas.',
    'account.displayName': 'Nombre visible',
    'account.displayNameHint': 'Se muestra en el menu de cuenta y en la app.',
    'account.displayNamePlaceholder': 'Como quieres que aparezca tu nombre',
    'account.language': 'Idioma',
    'account.language.english': 'Ingles',
    'account.language.spanish': 'Espanol',
    'account.timezone': 'Zona horaria',
    'account.dateFormat': 'Formato de fecha',
    'account.theme': 'Tema',
    'account.theme.light': 'Claro',
    'account.theme.dark': 'Oscuro',
    'account.theme.system': 'Sistema',
    'account.startPage': 'Pagina inicial',
    'account.startPageHint': 'A donde quieres llegar despues de iniciar sesion.',
    'account.savePreferences': 'Guardar preferencias',
    'account.preferencesUpdated': 'Preferencias de la cuenta actualizadas',
    'account.preferencesUpdateFailed': 'No se pudieron actualizar las preferencias de la cuenta',
    'account.photo': 'Foto de perfil',
    'account.photoHint': 'Se muestra en el avatar superior derecho y en el panel de cuenta. La recortamos automaticamente al centro antes de guardar.',
    'account.photoUpload': 'Subir foto',
    'account.photoChange': 'Cambiar foto',
    'account.photoSave': 'Guardar foto',
    'account.photoUpdated': 'Foto de perfil actualizada',
    'account.photoRemoved': 'Foto de perfil eliminada',
    'account.photoUpdateFailed': 'No se pudo actualizar la foto',
    'account.photoTooLarge': 'La foto debe pesar menos de 5 MB',
    'account.photoProcessed': 'Foto recortada y optimizada',
    'account.photoProcessFailed': 'No se pudo procesar la foto',
    'account.notifications': 'Preferencias de notificaciones',
    'account.notificationsHint': 'Controla que eventos generan notificaciones y como las recibes.',
    'account.inappSection': 'Notificaciones en la app',
    'account.inappSectionHint': 'Elige que alertas aparecen en la campanita de notificaciones.',
    'account.emailSection': 'Notificaciones por correo',
    'account.emailSectionHint': 'Elige que alertas tambien se envian a tu correo electronico.',
    'account.notifyMissingTimesheets': 'Recordatorios de timesheets faltantes',
    'account.notifyMissingTimesheetsHint': 'Alertas cuando tu equipo aun tiene timesheets pendientes.',
    'account.notifyStatusChanges': 'Timesheet aprobado / rechazado',
    'account.notifyStatusChangesHint': 'Alertas por cambios de estado en timesheets que te afectan.',
    'account.notifyProjectAssignments': 'Cambios de asignacion de proyecto',
    'account.notifyProjectAssignmentsHint': 'Recibe avisos cuando te asignan o te quitan de un proyecto.',
    'account.notifyProjectDelays': 'Alertas de retraso / riesgo del proyecto',
    'account.notifyProjectDelaysHint': 'Avisos cuando un proyecto se atrasa o entra en riesgo.',
    'account.notifyMaintenance': 'Alertas de mantenimiento',
    'account.notifyMaintenanceHint': 'Avisos previos antes de que comience una ventana de mantenimiento.',
    'account.notifySecurity': 'Alertas de actividad de seguridad',
    'account.notifySecurityHint': 'Inicios de sesion sospechosos, sesiones revocadas y otros eventos de seguridad.',
    'account.pendingApprovalDays': 'Alerta por aprobacion pendiente (dias)',
    'account.pendingApprovalDaysHint': 'Cuanto tiempo puede esperar algo antes de que quieras un recordatorio.',
    'account.saveNotifications': 'Guardar preferencias de notificaciones',
    'account.notificationsUpdated': 'Preferencias de notificaciones actualizadas',
    'account.notificationsUpdateFailed': 'No se pudieron actualizar las notificaciones',
    'account.security': 'Seguridad de la cuenta',
    'account.securityHint': 'Sesion actual, dispositivos reconocidos y actividad reciente de acceso.',
    'account.signOutAllDevices': 'Cerrar sesion en todos los dispositivos',
    'account.signingOut': 'Cerrando sesion...',
    'account.lastSuccessfulLogin': 'Ultimo acceso exitoso',
    'account.activeSessions': 'Sesiones activas',
    'account.currentDeviceIdentified': 'Dispositivo actual identificado',
    'account.sessionTrackingActive': 'Seguimiento de sesiones activo',
    'account.recognizedDevices': 'Dispositivos reconocidos',
    'account.noDevices': 'Aun no hay dispositivos registrados.',
    'account.unnamedDevice': 'Dispositivo sin nombre',
    'account.lastSeen': 'Ultima vez visto {{value}}',
    'account.ipLabel': 'IP {{value}}',
    'account.sessionCount': '{{count}} sesion',
    'account.sessionCountPlural': '{{count}} sesiones',
    'account.loadingSecurity': 'Cargando resumen de seguridad...',
    'account.loadingSessions': 'Cargando sesiones...',
    'account.loadingActivity': 'Cargando actividad...',
    'account.changePassword': 'Cambiar contrasena',
    'account.currentPassword': 'Contrasena actual',
    'account.currentPasswordPlaceholder': 'Tu contrasena actual',
    'account.newPassword': 'Nueva contrasena',
    'account.newPasswordPlaceholder': 'Minimo 8 caracteres',
    'account.confirmNewPassword': 'Confirmar nueva contrasena',
    'account.confirmNewPasswordPlaceholder': 'Repite la nueva contrasena',
    'account.updatePassword': 'Actualizar contrasena',
    'account.passwordUpdated': 'Contrasena actualizada',
    'account.passwordUpdateFailed': 'No se pudo actualizar la contrasena',
    'account.currentPasswordRequired': 'Ingresa tu contrasena actual',
    'account.passwordMinLength': 'La nueva contrasena debe tener al menos 8 caracteres',
    'account.passwordMismatch': 'Las contrasenas no coinciden',
    'account.activeSessionsTitle': 'Sesiones activas',
    'account.activeSessionsHint': 'Revisa donde esta iniciada tu cuenta y revoca lo que no reconozcas.',
    'account.noSessions': 'No se encontraron sesiones activas.',
    'account.sessionLabel': 'Sesion {{id}}',
    'account.lastUsed': 'Ultimo uso {{value}}',
    'account.expires': 'Expira {{value}}',
    'account.revoke': 'Revocar',
    'account.revoking': 'Revocando...',
    'account.sessionRevoked': 'Sesion revocada',
    'account.sessionRevokeFailed': 'No se pudo revocar la sesion',
    'account.signOutAllFailed': 'No se pudo cerrar la sesion en todos los dispositivos',
    'account.recentSecurityActivity': 'Actividad reciente de seguridad',
    'account.recentSecurityActivityHint': 'Inicios y cierres de sesion, refresh y cambios de contrasena recientes en esta cuenta.',
    'account.noSecurityActivity': 'No se encontro actividad reciente de seguridad.',
    'account.securityAction.login': 'Inicio de sesion',
    'account.securityAction.logout': 'Cierre de sesion',
    'account.securityAction.refresh': 'Refresh de sesion',
    'account.securityAction.password_change': 'Cambio de contrasena',
    'account.securityAction.revoke_session': 'Sesion revocada',
    'account.failedToLoadSecurity': 'No se pudo cargar la seguridad de la cuenta',

    'startPage.default': 'Inicio predeterminado',
    'startPage.dashboard': 'Panel',
    'startPage.timesheets': 'Timesheets',
    'startPage.projects': 'Proyectos',
    'startPage.schedule': 'Cronograma',
    'startPage.people': 'Personal',
    'startPage.reports': 'Reportes',
    'startPage.fieldLogs': 'Field Reports',
    'startPage.control': 'Control',
    'startPage.employeeHours': 'Horas de empleados',
    'startPage.exports': 'Exportaciones',
    'startPage.settings': 'Ajustes',
    'startPage.security': 'Centro de seguridad',

    'login.welcomeBack': 'Bienvenido de nuevo',
    'login.titlePrefix': 'Entra a',
    'login.titleAccent': 'tu obra.',
    'login.subtitle': 'Cuadrillas, presupuestos, cronogramas y reportes de campo - todo conectado. Un solo acceso.',
    'login.tagline': 'Forja lo que construyes.',
    'login.step': 'Paso',
    'login.emailStepTitle': 'Correo electronico',
    'login.passwordStepTitle': 'Ingresa tu contrasena',
    'login.sessionExpired': 'Tu sesion expiro. Vuelve a iniciar sesion.',
    'login.tryAgainIn': 'Intentalo de nuevo en {{value}}.',
    'login.slowNotice': 'Te estamos iniciando sesion. Esto esta tardando mas de lo habitual.',
    'login.emailPlaceholder': 'tu@empresa.com',
    'login.passwordPlaceholder': '********',
    'login.changeEmail': 'Cambiar correo',
    'login.signingIn': 'Entrando...',
    'login.continue': 'Continuar',
    'login.locked': 'Bloqueado',
    'login.connecting': 'Conectando',
    'login.session': 'Sesion',
    'login.waitAndRetry': 'Si la pagina no continua, espera un momento e intentalo otra vez. Tu sesion expira por inactividad y te pedira volver a iniciar sesion.',
    'login.waitAndRetryShort': 'Si la pagina no continua, espera un momento e intentalo otra vez. Tu sesion puede pedirte volver a iniciar sesion tras inactividad.',
    'login.status.rateLimited': 'Demasiados intentos para este correo. Intentalo de nuevo en {{value}}.',
    'login.status.slow': 'Te estamos iniciando sesion. El servidor esta respondiendo mas lento de lo habitual.',
    'login.status.timeout': 'La solicitud expiro antes de que el servidor respondiera.',
    'login.status.network': 'No pudimos conectarnos al servidor. Revisa la conexion e intentalo otra vez.',

    'error.forbidden.label': 'ACCESO DENEGADO',
    'error.forbidden.headline': 'No tienes acceso a esta area.',
    'error.forbidden.message': 'Tu cuenta no tiene permiso para ver esta pagina. Contacta a tu administrador si crees que es un error.',
    'error.server.label': 'SERVICIO NO DISPONIBLE',
    'error.server.headline': 'Estamos bajo el casco por un momento.',
    'error.server.message': 'Forxa no puede conectarse al backend en este momento. Asegurate de que el servidor este corriendo e intentalo de nuevo en un instante.',
    'error.data.label': 'ERROR DE DATOS',
    'error.data.headline': 'Algo regreso mal.',
    'error.data.message': 'La solicitud termino, pero la respuesta no fue valida. Refresca la pagina o intenta de nuevo; si sigue pasando, revisa el backend.',
    'error.default.label': 'NO ENCONTRADO',
    'error.default.headline': 'Aqui no hay nada mas que tierra y grava.',
    'error.default.message': 'La pagina que buscas no existe o fue movida. Vuelve al panel e intentalo otra vez.',
    'error.dashboard': 'Panel',
    'error.goBack': 'Volver',
    'error.weWillBeBack': 'Volveremos a las {{value}}',
    'error.footer': 'Error - {{code}} - Forxa',

    'maintenance.inProgress': 'Mantenimiento en curso',
    'maintenance.unavailable': 'El sistema esta temporalmente no disponible.',
    'maintenance.backAt': 'Disponible a las {{value}}.',
    'maintenance.scheduled': 'Mantenimiento programado {{value}}',
    'maintenance.shortly': 'en breve',
    'maintenance.inHoursMinutes': 'en {{hours}}h {{minutes}}m',
    'maintenance.inMinutes': 'en {{minutes}}m',
  },
};

function interpolate(template: string, params?: TranslationParams) {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? ''));
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (value: Language) => void;
  isSpanish: boolean;
  t: (key: string, params?: TranslationParams, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({
  children,
  preferredLanguage,
}: {
  children: React.ReactNode;
  preferredLanguage?: string | null;
}) {
  const [language, setLanguage] = useState<Language>(preferredLanguage === 'es' ? 'es' : 'en');

  useEffect(() => {
    setLanguage(preferredLanguage === 'es' ? 'es' : 'en');
  }, [preferredLanguage]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('forxa-language', language);
    }
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    setLanguage,
    isSpanish: language === 'es',
    t: (key, params, fallback) => {
      const localized = dictionaries[language][key];
      const english = dictionaries.en[key];
      return interpolate(localized || english || fallback || key, params);
    },
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
